import { useRef } from "react";
import { useMutation, useQueryClient } from "react-query";

import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { getOneProductKey } from "./useGetOneProduct";
import { productGridKey } from "./useGetAllproduct";
import { getProductScoresKey } from "../olapScores/useGetProductScores";
import { getProductPostKey } from "../post/useGetProductPost";
import { getProductPostsKey } from "../post/useGetProductPosts";

type UpsertProduct = {
    product: ProductWrite;
    organizationId?: number;
};

type UseUpsertProductOptions = {
    quarantineAmbiguousCreate?: boolean;
};

type CreateRequestState = "idle" | "pending" | "created" | "identity-unknown";

const isExplicitClientRejection = (error: unknown): boolean => {
    const status = (error as { response?: { status?: unknown } })?.response?.status;
    return typeof status === "number" && status >= 400 && status < 500;
};

const hasPersistedProductIdentity = (response: unknown): boolean => {
    const id = (response as { data?: { id?: unknown } })?.data?.id;
    return typeof id === "number" && Number.isInteger(id) && id > 0;
};

export const useUpsertProduct = ({ quarantineAmbiguousCreate = false }: UseUpsertProductOptions = {}) => {
    const queryClient = useQueryClient();
    const createRequestState = useRef<CreateRequestState>("idle");

    const quarantineCreate = () => {
        createRequestState.current = "identity-unknown";
    };

    const query = useMutation({
        mutationFn: async (options: UpsertProduct) => {
            const { product, organizationId } = options;

            if (product.id !== undefined) {
                return await loggedInApiCall.put("/api/products/" + product.id, product, {
                    params: { organization_id: organizationId },
                });
            }

            if (organizationId === undefined) {
                throw new Error("Creating a product requires the owning organization id.");
            }

            const { ownedBy: _pinnedFromPath, ...payload } = product;

            if (!quarantineAmbiguousCreate) {
                return await loggedInApiCall.post(`/api/organizations/${organizationId}/products`, payload);
            }

            if (createRequestState.current !== "idle") {
                throw new Error("Product creation is already pending or its persisted identity is unknown.");
            }

            createRequestState.current = "pending";
            let responseIdentityIsUnknown = false;

            try {
                const response = await loggedInApiCall.post(`/api/organizations/${organizationId}/products`, payload);
                if (!hasPersistedProductIdentity(response)) {
                    responseIdentityIsUnknown = true;
                    quarantineCreate();
                    throw new Error("The product creation response does not expose a persisted identity.");
                }

                createRequestState.current = "created";
                return response;
            } catch (error) {
                if (!responseIdentityIsUnknown) {
                    if (isExplicitClientRejection(error)) {
                        createRequestState.current = "idle";
                    } else {
                        quarantineCreate();
                    }
                }

                throw error;
            }
        },
        onSuccess: (_response, options: UpsertProduct) => {
            const { id: productId } = options.product;

            if (productId === undefined) return;

            queryClient.invalidateQueries([productGridKey]);
            queryClient.invalidateQueries([getOneProductKey]);
            queryClient.invalidateQueries([getOneProductKey, productId]);
            queryClient.invalidateQueries([getProductScoresKey, productId]);
            queryClient.invalidateQueries([getProductPostKey, productId]);
            queryClient.invalidateQueries([getProductPostsKey, productId]);
        },
    });

    return {
        ...query,
        get isCreateIdentityUnknown() {
            return quarantineAmbiguousCreate && createRequestState.current === "identity-unknown";
        },
    };
};

type PPWriteFields = {
    quantity: number | null;
    usedWeight?: number | null;
};

export type ProductPackagingReferenceKind = "element" | "composite";
export type ProductPackagingWrite =
    | ({ "@id": IRI } & PPWriteFields)
    | ({ packaging: IRI } & PPWriteFields)
    | ({ packagingReferenceId: number; packagingReferenceKind: ProductPackagingReferenceKind } & PPWriteFields);

export type ProductTransformationWrite =
    | IRI
    | {
          "@id"?: string;
          transformationReferenceId: number;
          massGrams?: number | null;
          organization?: IRI | null;
          position?: number;
      };

type PIWriteFields = {
    quantity: number | null;
    // proportion: number | null;
    usedQuantity: number | null;
    // usedProportion: number | null;
    isUsedQuantityEqualToQuantity: boolean;
};
export type ProductIngredientWrite =
    | ({ "@id": IRI } & PIWriteFields)
    | ({ "@id": IRI; ingredient: IRI } & PIWriteFields)
    | ({ "@id": IRI; intermediateProduct: IRI } & PIWriteFields)
    | ({
          "@id": IRI;
          ingredientReferenceId: number;
          referenceSource: "sure" | "foodpilot";
      } & PIWriteFields)
    | ({ ingredient: IRI } & PIWriteFields)
    | ({ intermediateProduct: IRI } & PIWriteFields)
    | ({ ingredientReferenceId: number; referenceSource: "sure" | "foodpilot" } & PIWriteFields);

export type ProductCompanyWrite =
    | IRI
    | {
          "@id"?: string;
          organization: IRI;
      };

export type ProductConsumptionWrite =
    | IRI
    | {
          "@id"?: string;
          consumptionReferenceId: number;
      };

export type TransportStage =
    | "ingredient_to_processing_site"
    | "packaging_to_processing_site"
    | "product_to_storage"
    | "storage_to_retail"
    | "retail_to_consumer"
    | "custom";

export type ProductTransportWrite = {
    "@id"?: string;
    productIngredient?: IRI | null;
    productPackaging?: IRI | null;
    stage: TransportStage;
    transportCircuit: IRI;
};

export type ProductWrite = {
    id: number | undefined;

    title: string | null;
    clientId: string | null;
    productReferenceId: number | null;
    netWeight: number | null;
    brand: IRI | null;
    productLine: IRI | null;
    jsonAttributes?: Record<string, any>;
    tags?: Record<string, string[]>;
    productIngredients: ProductIngredientWrite[];
    productPackagings: ProductPackagingWrite[];
    shares?: ProductCompanyWrite[];
    productConsumptions?: ProductConsumptionWrite[];
    productTransformations?: ProductTransformationWrite[];
    productTransports?: ProductTransportWrite[];
    distributionReferenceId?: number | null;
    distribution?: IRI | null;

    ownedBy?: IRI;
    ssqId?: number | null;
    isIntermediate?: boolean;
    organic?: boolean;
};
